import {TEXT_ON_PATH_PLACEHOLDER_TEXT_KEY} from '@PosterWhiteboard/items/text-item/text-shape.types';

const PLACEHOLDER_SEPARATOR = ' ';

/** "add your text", repeated `repeatCount` times with a space between repeats. */
export const getTextOnPathPlaceholder = (repeatCount: number): string => {
  const unit = window.i18next.t(TEXT_ON_PATH_PLACEHOLDER_TEXT_KEY);
  return Array.from({length: Math.max(1, repeatCount)}, (): string => unit).join(PLACEHOLDER_SEPARATOR);
};

/**
 * True for an untouched placeholder: one whole "add your text", followed by any stretch of its
 * repeats, wherever they were cut. Any edit makes it the user's own text.
 */
export const isTextOnPathPlaceholder = (text: string): boolean => {
  const unit = window.i18next.t(TEXT_ON_PATH_PLACEHOLDER_TEXT_KEY);
  if (text.length < unit.length) {
    return false;
  }
  return getTextOnPathPlaceholder(Math.ceil(text.length / (unit.length + PLACEHOLDER_SEPARATOR.length)) + 1).startsWith(text);
};

export interface TextOnPathPlaceholderFitInput {
  pathLength: number;
  /** The width a text takes on one line at the item's font, in the path's units. */
  measureTextWidth: (text: string) => number;
}

/**
 * The placeholder for a path of `pathLength` at the item's font: "add your text" repeated from
 * the path's start and cut wherever the path ends, mid-word if need be, so every new path keeps
 * one font size and is filled end to end. The first "add your text" always shows whole: a path
 * shorter than it gets it whole anyway, and the font shrinks to fit.
 */
export const getTextOnPathPlaceholderFillingPath = ({pathLength, measureTextWidth}: TextOnPathPlaceholderFitInput): string => {
  const widthPerRepeat = measureTextWidth(getTextOnPathPlaceholder(2)) - measureTextWidth(getTextOnPathPlaceholder(1));
  const repeatCount = widthPerRepeat > 0 ? Math.ceil(pathLength / widthPerRepeat) + 1 : 1;
  const graphemes = splitIntoGraphemes(getTextOnPathPlaceholder(repeatCount));
  const getPrefix = (graphemeCount: number): string => graphemes.slice(0, graphemeCount).join('').trimEnd();
  let fitting = splitIntoGraphemes(getTextOnPathPlaceholder(1)).length;
  let overflowing = graphemes.length + 1;
  while (overflowing - fitting > 1) {
    const middle = Math.floor((fitting + overflowing) / 2);
    if (measureTextWidth(getPrefix(middle)) <= pathLength) {
      fitting = middle;
    } else {
      overflowing = middle;
    }
  }
  return getPrefix(fitting);
};

/** Whole user-perceived letters, so a cut never splits a letter from its combining marks (Thai, Devanagari, emoji). */
const splitIntoGraphemes = (text: string): string[] => {
  return Array.from(new Intl.Segmenter(undefined, {granularity: 'grapheme'}).segment(text), ({segment}): string => segment);
};
